import { FC, useContext, useMemo } from 'react';
import { InvoiceTabEnum } from '../constants';
import { AutoFilledIndicator, CustomSwitch } from 'components';
import CustomInput, { InputType } from 'components/custom-input.tsx/CustomInput';
import { useAppSelector } from 'hooks';
import { selectInvoiceAutoFilledData } from 'containers/invoices/InvoicesSlice';

interface MoreOptionsSectionProps {
  isDisabled: boolean;
  className?: string;
  context: React.Context<any>;
  signatureState: boolean;
  updateSignatureState: (value: boolean) => void;
  id: string;
  invoiceReferenceNumber: string;
  updateInvoiceReferenceNumber: (value: string) => void;
}

const MoreOptionsSection: FC<MoreOptionsSectionProps> = ({
  isDisabled,
  context,
  signatureState,
  updateSignatureState,
  id,
  invoiceReferenceNumber,
  updateInvoiceReferenceNumber
}) => {
  const { selectedInvoiceTab, setSelectedInvoiceTab } = useContext<any>(context);
  const autoFilledData = useAppSelector(selectInvoiceAutoFilledData);
  const isTabSelected = selectedInvoiceTab === InvoiceTabEnum.MORE_OPTIONS;

  const onSignatureConfigChange = () => {
    setSelectedInvoiceTab(InvoiceTabEnum.MORE_OPTIONS);
    updateSignatureState(!signatureState);
  };

  const isReferenceNumberAutoFilled = useMemo(() => {
    if (autoFilledData && autoFilledData?.invoice.referenceId)
      return autoFilledData?.invoice?.referenceId?.toString() === invoiceReferenceNumber?.toString();
    return false;
  }, [invoiceReferenceNumber, autoFilledData?.invoice?.referenceId]);

  return (
    <div>
      <div className={`mb-2 text-xl font-bold ${isTabSelected ? 'text-primaryBtn' : 'text-heading'}`}>More Options</div>
      <div
        className={`flex flex-col rounded-md border px-[30px] py-5 1439px:p-10 
                    ${isTabSelected ? 'border-primaryBtn' : 'border-borderGray'}`}>
        <div className={`relative flex flex-row items-center gap-4 ${isDisabled ? 'pointer-events-none' : ''}`}>
          {isDisabled && <div className="slant-lines absolute left-0 z-50 h-full w-full rounded opacity-40" />}

          <CustomSwitch enabled={signatureState} toggleSwitch={onSignatureConfigChange} id={id} />

          <div className="flex flex-col">
            <div className="text-base font-medium text-primaryText">Request a customer signature</div>
            <div className="text-sm text-gray-600">
              Signatures help better establish payment consent and will be added to receipts and invoices
            </div>
          </div>
        </div>

        {/* Invoice Reference Number Section */}
        <div className="mt-6">
          <CustomInput
            id="invoice-reference-number-input"
            type={InputType.TEXT}
            value={invoiceReferenceNumber}
            onChange={updateInvoiceReferenceNumber}
            placeholder="Enter invoice reference number here"
            label={
              <div className="flex items-center">
                Invoice reference number
                <AutoFilledIndicator isAutoFilled={isReferenceNumberAutoFilled} />
              </div>
            }
            className="bg-secondaryBtn"
          />
        </div>
      </div>
    </div>
  );
};

export default MoreOptionsSection;
